<template>
  <div class="py-6 px-8">
    <h-header>
      <template v-slot:actions>
        <v-btn
          @click="openCreateRealtyModal"
          color="primary"
          elevation="0"
        >
          <v-icon left>mdi-plus</v-icon>
          <span class="d-none d-md-flex body-2">
            {{ $t(`create.label.realty`) }}
          </span>
        </v-btn>
      </template>
    </h-header>

    <RealtyListFilters />

    <v-card outlined>
      <v-data-table
        disable-sort
        hide-default-footer
        mobile-breakpoint="20"
        :headers="headers"
        :items="realties"
        :loading="isLoading"
        :items-per-page="itemsPerPage"
        :no-data-text="$t('realty.table.noData')"
        :loading-text="$t('realty.table.loading')"
        @dblclick:row="showRealtyRow"
        class="clickable"
      >
        <template v-slot:[`item.code`]="{ item }">
          <span v-if="item.address">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip small>
                  <span
                    v-on="on"
                    v-bind="attrs"
                    class="d-block mw-100 text-truncate"
                  >
                    {{ item.code }}
                  </span>
                </v-chip>
              </template>
              {{ item.code }}
            </v-tooltip>
          </span>
        </template>
        <template v-slot:[`item.address`]="{ item }">
          <span v-if="item.address">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-on="on"
                  v-bind="attrs"
                  class="d-block mw-250 text-truncate"
                >
                  {{ item.address }}
                </span>
              </template>
              {{ item.address }}
            </v-tooltip>
          </span>
        </template>
        <template v-slot:[`item.additional_address_data`]="{ item }">
          <span v-if="item.additional_address_data">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-on="on"
                  v-bind="attrs"
                  class="d-block mw-250 text-truncate"
                >
                    {{ item.additional_address_data }}
                  </span>
                  </template>
                {{ item.additional_address_data }}
              </v-tooltip>
            </span>
          <span v-else>—</span>
        </template>
        <template v-slot:[`item.furnished`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-on="on"
                v-bind="attrs"
                :color="furnishingIcon(item).color"
                small
              >
                {{ furnishingIcon(item).icon }}
              </v-icon>
            </template>
            {{ furnishingText(item) }}
          </v-tooltip>
        </template>
        <template v-slot:[`item.trousseau`]="{ item }">
          <v-icon :color="item.trousseau ? 'success' : 'error'" small>
            {{ item.trousseau ? 'mdi-check' : 'mdi-close' }}
          </v-icon>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-end space-between">
            <v-btn @click="showRealty(item)" class="mx-1" icon>
              <v-icon small> mdi-eye </v-icon>
            </v-btn>
            <v-btn @click="openUpdateModal(item.id)" class="mx-1" icon>
              <v-icon small> mdi-pencil </v-icon>
            </v-btn>
            <v-btn @click="openDeleteModal(item.id, item.code)" class="mx-1" icon>
              <v-icon small> mdi-delete </v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <DeleteModal
      :callback="fetchRealties"
      toDelete="deleteRealty"
      ref="deleteRealtyModal"
    />

    <RightDrawer :moreInfo="false">
      <RealtySummary :realty="details" />
    </RightDrawer>

    <h-pagination :fetch="fetchRealties" />
  </div>
</template>
<script>
import RealtyListFilters from '@/components/realty/RealtyListFilters'
import RealtyCreateUpdate from '@/components/realty/RealtyCreateUpdate'
import RealtySummary from '@/components/realty/RealtySummary'
import DeleteModal from '@/components/app/DeleteModal'
import RightDrawer from '@/components/app/RightDrawer'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { realtyHeaders } from './listHeaders/realtyHeaders'
import { handleParams } from '@/util/routes'
import { FURNISHING, furnishingKey, toFurnishing } from '@/util/furnishing'

// A realty with no furnishing information shows as unfurnished, as it did before semi-furnished
const FURNISHING_ICONS = {
  [FURNISHING.FURNISHED]: { icon: 'mdi-check', color: 'success' },
  [FURNISHING.SEMI_FURNISHED]: { icon: 'mdi-circle-half-full', color: 'warning' },
  [FURNISHING.NOT_FURNISHED]: { icon: 'mdi-close', color: 'error' },
}

export default {
  name: 'RealtyList',

  components: {
    DeleteModal,
    RightDrawer,
    RealtySummary,
    RealtyListFilters,
    RealtyCreateUpdate,
  },

  data() {
    return {
      headers: realtyHeaders,
      details: {},
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'realties', 'itemsPerPage']),

    isUserLoggedIn() {
      const token = localStorage.getItem('access_token')
      return !!token
    },
  },

  methods: {
    ...mapActions(['fetchRealties']),

    ...mapMutations(['setIsDrawerOpen']),

    furnishingIcon(realty) {
      const furnishing = toFurnishing(realty.furnished, realty.semi_furnished)
      return FURNISHING_ICONS[furnishing ?? FURNISHING.NOT_FURNISHED]
    },

    furnishingText(realty) {
      const key = furnishingKey(realty)
      return key ? this.$t(key) : '---'
    },

    openDeleteModal(realtyId, realtyCode) {
      this.$refs.deleteRealtyModal.openModal(realtyId, realtyCode)
    },

    openUpdateModal(realtyId) {
      const realtyModal = this.$root.$children[0].$refs?.createUpdateRealtyModal
      this.$nextTick(realtyModal.openRealtyCreateModal())
      this.$nextTick(() => realtyModal.fetchRealtyData(realtyId))
    },

    showRealty(item) {
      this.details = item
      this.setIsDrawerOpen(true)
      handleParams('realties', item.id)
    },

    showRealtyRow(_, { item }) {
      this.showRealty(item)
    },

    openCreateRealtyModal() {
      this.$root.$children[0].$refs?.createUpdateRealtyModal.openRealtyCreateModal()
    }
  },

  mounted() {
    if (!this.isUserLoggedIn) return
    this.fetchRealties()
  },
}
</script>
